import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  specificationId: "",
  value: "",
  order: 0,
  isActive: true
});

export function ProductSpecificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const res = await apiGet(`/api/ProductSpecifications/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product specification");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        specificationId: x.specificationId ?? "",
        value: x.value ?? "",
        order: x.order ?? 0,
        isActive: Boolean(x.isActive)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.specificationId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductSpecifications", {
      id: editingId ?? 0,
      specificationId: Number(form.specificationId),
      value: form.value || null,
      order: Number(form.order || 0),
      isActive: form.isActive
    });
    if (!res.ok) setError(res.error ?? "Failed to save product specification");
    else {
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductSpecifications/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product specification");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        specificationId: x.specificationId ?? "",
        value: x.value ?? "",
        order: x.order ?? 0,
        isActive: Boolean(x.isActive)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product Specification" : "Add Product Specification"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-specifications-form"
            disabled={saving}
            title="Save"
            aria-label="Save product specification"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-specifications-list" className="icon-button icon-button--toolbar" title="Specifications list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-specifications-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Specification Id</span>
          <input type="number" value={form.specificationId} onChange={(e) => setForm((p) => ({ ...p, specificationId: e.target.value }))} />
        </label>
        <label className="field">
          <span>Value</span>
          <input value={form.value} onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))} />
        </label>
        <label className="field">
          <span>Order</span>
          <input type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))} />
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
          <span>Is Active</span>
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
